import React from 'react';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import { motion } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Check, MapPin, Phone, Mail, GraduationCap, Ship, Award, ArrowRight, Clock, Sparkles, Landmark } from 'lucide-react';

// Infographic components
import EcossistemaSFSSection from '@/components/saofrancisco/EcossistemaSFSSection';
import DestinosSFSSection from '@/components/saofrancisco/DestinosSFSSection';
import NumerosSFSSection from '@/components/saofrancisco/NumerosSFSSection';


// =====================================================
// 1. HERO SFS (Redesigned)
// =====================================================
const HeroSFS: React.FC = () => (
    <section className="relative min-h-[85vh] flex items-center justify-center text-center text-white overflow-hidden">
        <div className="absolute inset-0 z-0">
            <img src="/assets/Sfs.jpg" alt="São Francisco do Sul - Cidade Histórica e Portuária" className="w-full h-full object-cover scale-105" />
        </div>
        <div className="absolute inset-0 z-10 bg-gradient-to-b from-primary/80 via-primary/50 to-primary/90" />
        <div
            className="absolute inset-0 z-10 opacity-[0.03] pointer-events-none"
            style={{
                backgroundImage: 'radial-gradient(circle at 1px 1px, white 1px, transparent 0)',
                backgroundSize: '32px 32px',
            }}
        />

        <div className="relative z-20 container mx-auto px-6">
            <motion.div
                initial={{ opacity: 0, y: -20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8 }}
            >
                <div className="inline-flex items-center gap-2 px-5 py-2 mb-8 rounded-full bg-gold/20 text-gold font-bold tracking-wider text-sm border border-gold/30">
                    <Landmark className="w-4 h-4" />
                    UNIDADE SÃO FRANCISCO DO SUL
                </div>
                <h1 className="font-heading text-4xl sm:text-5xl md:text-6xl lg:text-7xl leading-tight max-w-5xl mx-auto">
                    Tradição e Mar se Encontram em <span className="text-gold">São Chico</span>
                </h1>
                <p className="mt-6 text-lg md:text-xl max-w-3xl mx-auto text-white/80 font-light leading-relaxed">
                    Na terceira cidade mais antiga do Brasil, onde porto, pesca e lazer coexistem — a JL Escola Náutica traz excelência para cada navegador.
                </p>
                <div className="mt-10 flex flex-col sm:flex-row gap-4 justify-center">
                    <a href="#nossos-servicos">
                        <Button size="lg" className="btn-gold w-full sm:w-auto text-lg px-8 py-6">
                            Descubra Nossos Serviços
                        </Button>
                    </a>
                    <a href="#contato-sfs">
                        <Button size="lg" variant="outline" className="w-full sm:w-auto border-gold text-gold hover:bg-gold/10 hover:text-gold">
                            Fale Conosco
                        </Button>
                    </a>
                </div>
            </motion.div>

            {/* Stats Strip */}
            <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.4 }}
                className="grid grid-cols-2 md:grid-cols-4 gap-4 max-w-4xl mx-auto mt-16"
            >
                {[
                    { value: '520+', label: 'Anos de História' },
                    { value: '130 km²', label: 'Baía da Babitonga' },
                    { value: '12+', label: 'Destinos Navegáveis' },
                    { value: 'Top 5', label: 'Porto do Brasil' },
                ].map((stat) => (
                    <div
                        key={stat.label}
                        className="bg-white/5 backdrop-blur-sm p-5 rounded-xl border border-white/10 hover:border-gold/30 transition-all duration-300"
                    >
                        <div className="text-3xl font-bold text-gold">{stat.value}</div>
                        <div className="text-xs text-white/60 uppercase tracking-wider mt-1">{stat.label}</div>
                    </div>
                ))}
            </motion.div>
        </div>
    </section>
);


// =====================================================
// 2. WELCOME SECTION (Enhanced)
// =====================================================
const WelcomeSFSSection: React.FC = () => (
    <section className="py-24 sm:py-32 bg-background">
        <div className="container mx-auto px-6 grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div
                initial={{ opacity: 0, x: -40 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true, amount: 0.3 }}
                transition={{ duration: 0.8 }}
            >
                <span className="inline-block px-4 py-1.5 rounded-full bg-gold/10 text-gold text-sm font-semibold tracking-wider uppercase mb-6">
                    Bem-vindo
                </span>
                <h2 className="font-heading text-3xl sm:text-4xl md:text-5xl text-primary mb-6">
                    A JL Chega à Histórica São Chico
                </h2>
                <p className="text-lg text-muted-foreground leading-relaxed mb-4">
                    São Francisco do Sul não é apenas uma cidade no mapa — é uma <strong className="text-primary">referência marítima nacional</strong>. Aqui, o porto que movimenta o Brasil, a pesca artesanal que alimenta gerações e o lazer náutico da Baía da Babitonga criam uma comunidade que <strong className="text-primary">vive e respira o mar</strong>.
                </p>
                <p className="text-lg text-muted-foreground leading-relaxed mb-6">
                    A JL Escola Náutica chegou a São Chico para atender <strong className="text-primary">cada perfil dessa comunidade</strong> — do pescador artesanal que precisa regularizar seu barco ao veranista do Caprite Club que quer tirar sua habilitação. Formação, burocracia e consultoria, tudo em um só lugar.
                </p>
                <div className="flex flex-wrap gap-3">
                    {['Escola Náutica', 'Despachante Marítimo', 'Consultoria Premium'].map(tag => (
                        <span key={tag} className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-primary/5 border border-primary/10 rounded-full text-sm font-medium text-primary">
                            <Check className="w-3.5 h-3.5 text-gold" />
                            {tag}
                        </span>
                    ))}
                </div>
            </motion.div>
            <motion.div
                initial={{ opacity: 0, x: 40 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true, amount: 0.3 }}
                transition={{ duration: 0.8 }}
                className="relative"
            >
                <div
                    className="absolute -inset-4 rounded-2xl pointer-events-none animate-glow-pulse opacity-40"
                    style={{ boxShadow: '0 0 60px hsl(43 74% 53% / 0.1)' }}
                />
                <img
                    src="/assets/Aula pratica.png"
                    alt="Aula prática JL Escola Náutica São Francisco do Sul"
                    className="rounded-xl shadow-2xl relative z-10 w-full"
                />
                <div className="absolute top-4 left-4 z-20 bg-gold/90 backdrop-blur-sm text-primary text-xs font-bold uppercase tracking-wider px-3 py-1.5 rounded-full">
                    📍 Centro Histórico
                </div>
            </motion.div>
        </div>
    </section>
);


// =====================================================
// 3. SERVICES TABS (Enhanced)
// =====================================================
const ServicesSFSSection: React.FC = () => (
    <section id="nossos-servicos" className="py-24 sm:py-32 bg-primary/[0.03]">
        <div className="container mx-auto px-6">
            <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6 }}
                className="text-center mb-16"
            >
                <span className="inline-block px-4 py-1.5 rounded-full bg-gold/10 text-gold text-sm font-semibold tracking-wider uppercase mb-4">
                    Serviços
                </span>
                <h2 className="font-heading text-3xl sm:text-4xl md:text-5xl text-primary">
                    Do Porto ao Caprite Club: Soluções Completas
                </h2>
                <p className="mt-4 text-lg text-muted-foreground max-w-3xl mx-auto">
                    Nossos serviços atendem desde a pesca artesanal até o lazer de alto padrão na Baía da Babitonga.
                </p>
            </motion.div>

            <Tabs defaultValue="escola" className="w-full max-w-4xl mx-auto">
                <TabsList className="grid w-full grid-cols-1 sm:grid-cols-3 bg-cream border border-gold/20">
                    <TabsTrigger value="escola"><GraduationCap className="w-5 h-5 mr-2" /> Escola Náutica</TabsTrigger>
                    <TabsTrigger value="despachante"><Ship className="w-5 h-5 mr-2" /> Despachante</TabsTrigger>
                    <TabsTrigger value="premium"><Award className="w-5 h-5 mr-2" /> Consultoria Premium</TabsTrigger>
                </TabsList>

                <TabsContent value="escola" className="py-8 bg-white p-8 rounded-b-lg shadow-md border border-t-0 border-border/30">
                    <h3 className="font-heading text-2xl text-primary mb-4">Habilitação Náutica: Lazer e Profissão</h3>
                    <p className="text-muted-foreground mb-6 leading-relaxed">
                        Cursos de Arrais Amador, Motonauta e Mestre Amador com aulas práticas na Baía da Babitonga. Seja para passear de jet ski no Caprite, navegar com a família ou iniciar carreira profissional — temos o curso certo para você.
                    </p>
                    <ul className="space-y-3 mb-6 text-primary/90">
                        <li className="flex items-start gap-2"><Check className="w-5 h-5 text-gold flex-shrink-0 mt-0.5" /> Aulas práticas na Baía da Babitonga e entorno.</li>
                        <li className="flex items-start gap-2"><Check className="w-5 h-5 text-gold flex-shrink-0 mt-0.5" /> Instrutores com experiência em navegação costeira e portuária.</li>
                        <li className="flex items-start gap-2"><Check className="w-5 h-5 text-gold flex-shrink-0 mt-0.5" /> Preparação completa para provas da Marinha.</li>
                        <li className="flex items-start gap-2"><Check className="w-5 h-5 text-gold flex-shrink-0 mt-0.5" /> Opção de treinamento para pescadores profissionais.</li>
                    </ul>
                    <a href="/escola">
                        <Button className="btn-gold">Ver Detalhes dos Cursos <ArrowRight className="ml-2 w-4 h-4" /></Button>
                    </a>
                </TabsContent>

                <TabsContent value="despachante" className="py-8 bg-white p-8 rounded-b-lg shadow-md border border-t-0 border-border/30">
                    <h3 className="font-heading text-2xl text-primary mb-4">Despachante Náutico e Marítimo: Seu Parceiro no Porto</h3>
                    <p className="text-muted-foreground mb-6 leading-relaxed">
                        Em uma cidade portuária como SFS, a regularização exige expertise. Atendemos pescadores artesanais, proprietários de lanchas e empresas marítimas com agilidade e segurança documental.
                    </p>
                    <ul className="space-y-3 mb-6 text-primary/90">
                        <li className="flex items-start gap-2"><Check className="w-5 h-5 text-gold flex-shrink-0 mt-0.5" /> Inscrição e Registro de Embarcações de Lazer e Profissionais.</li>
                        <li className="flex items-start gap-2"><Check className="w-5 h-5 text-gold flex-shrink-0 mt-0.5" /> Transferência de Propriedade, licenciamento e vistoria.</li>
                        <li className="flex items-start gap-2"><Check className="w-5 h-5 text-gold flex-shrink-0 mt-0.5" /> Assessoria para embarcações de pesca artesanal e comercial.</li>
                        <li className="flex items-start gap-2"><Check className="w-5 h-5 text-gold flex-shrink-0 mt-0.5" /> Regularização junto à Capitania dos Portos de SFS.</li>
                    </ul>
                    <a href="/despachante">
                        <Button className="btn-gold">Saiba Mais sobre Despachante <ArrowRight className="ml-2 w-4 h-4" /></Button>
                    </a>
                </TabsContent>

                <TabsContent value="premium" className="py-8 bg-white p-8 rounded-b-lg shadow-md border border-t-0 border-border/30">
                    <h3 className="font-heading text-2xl text-primary mb-4">Consultoria Premium em São Chico</h3>
                    <p className="text-muted-foreground mb-6 leading-relaxed">
                        Para proprietários de iates, jet skis e embarcações de alto padrão na região de São Francisco do Sul. Aquisição segura, habilitação express e gestão completa — com consultores que conhecem cada canto da baía.
                    </p>
                    <ul className="space-y-3 mb-6 text-primary/90">
                        <li className="flex items-start gap-2"><Check className="w-5 h-5 text-gold flex-shrink-0 mt-0.5" /> Aquisição e vistoria de iates e lanchas de alto valor.</li>
                        <li className="flex items-start gap-2"><Check className="w-5 h-5 text-gold flex-shrink-0 mt-0.5" /> Treinamento VIP a bordo na Baía da Babitonga.</li>
                        <li className="flex items-start gap-2"><Check className="w-5 h-5 text-gold flex-shrink-0 mt-0.5" /> Concierge náutico para gestão total em São Chico.</li>
                        <li className="flex items-start gap-2"><Check className="w-5 h-5 text-gold flex-shrink-0 mt-0.5" /> Consultor dedicado com expertise na região norte de SC.</li>
                    </ul>
                    <a href="/consultoria-premium">
                        <Button className="btn-gold">Descobrir Consultoria Premium <ArrowRight className="ml-2 w-4 h-4" /></Button>
                    </a>
                </TabsContent>
            </Tabs>
        </div>
    </section>
);


// =====================================================
// 4. WHY JL IN SFS (Enhanced)
// =====================================================
const WhyJLSFSSection: React.FC = () => {
    const reasons = [
        {
            icon: <Landmark className="w-8 h-8 text-gold" />,
            title: "No Centro Histórico",
            description: "Localização no coração de São Chico, no Lgo. Almirante Moraes Rego — próximo ao porto, marinas e áreas pesqueiras."
        },
        {
            icon: <GraduationCap className="w-8 h-8 text-gold" />,
            title: "Instrutores da Baía",
            description: "Nossa equipe navega a Babitonga diariamente. Ensinamos correntes, ventos e rotas seguras da região que só quem vive aqui conhece."
        },
        {
            icon: <Ship className="w-8 h-8 text-gold" />,
            title: "Expertise Porto e Pesca",
            description: "Diferente de outras escolas, temos experiência com embarcações comerciais e de pesca — não só lazer."
        },
        {
            icon: <Clock className="w-8 h-8 text-gold" />,
            title: "Horários para Pescadores",
            description: "Flexibilidade para quem depende do mar para viver. Turmas especiais, horários compatíveis com a maré."
        }
    ];

    return (
        <section className="py-24 sm:py-32 bg-background">
            <div className="container mx-auto px-6">
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.6 }}
                    className="text-center mb-16"
                >
                    <span className="inline-block px-4 py-1.5 rounded-full bg-gold/10 text-gold text-sm font-semibold tracking-wider uppercase mb-4">
                        Diferenciais
                    </span>
                    <h2 className="font-heading text-3xl sm:text-4xl md:text-5xl text-primary">
                        Por Que a JL em São Chico?
                    </h2>
                    <p className="mt-4 text-lg text-muted-foreground max-w-2xl mx-auto">
                        Entendemos a realidade de quem vive do mar e de quem escolhe o mar como lazer.
                    </p>
                </motion.div>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 max-w-5xl mx-auto">
                    {reasons.map((reason, i) => (
                        <motion.div
                            key={reason.title}
                            initial={{ opacity: 0, y: 20 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true, amount: 0.5 }}
                            transition={{ duration: 0.5, delay: i * 0.1 }}
                            whileHover={{ scale: 1.03, y: -5 }}
                            className="text-center p-8 border border-border rounded-xl bg-white hover:shadow-xl hover:border-gold/30 transition-all duration-300 cursor-default"
                        >
                            <div className="flex justify-center mb-4">
                                <div className="w-14 h-14 rounded-full bg-gold/10 flex items-center justify-center">
                                    {reason.icon}
                                </div>
                            </div>
                            <h3 className="text-lg font-bold text-primary mb-2">{reason.title}</h3>
                            <p className="text-sm text-muted-foreground leading-relaxed">{reason.description}</p>
                        </motion.div>
                    ))}
                </div>
            </div>
        </section>
    );
};


// =====================================================
// 5. LOCATION SECTION (Enhanced)
// =====================================================
const LocationSFSSection: React.FC = () => (
    <section className="py-24 sm:py-32 bg-primary/[0.03]">
        <div className="container mx-auto px-6">
            <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6 }}
                className="text-center mb-12"
            >
                <span className="inline-block px-4 py-1.5 rounded-full bg-gold/10 text-gold text-sm font-semibold tracking-wider uppercase mb-4">
                    Localização
                </span>
                <h2 className="font-heading text-3xl sm:text-4xl md:text-5xl text-primary">Nossa Unidade em São Chico</h2>
                <p className="mt-4 text-lg text-muted-foreground max-w-2xl mx-auto">
                    No centro histórico, a poucos passos do porto e com acesso direto à baía.
                </p>
            </motion.div>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-start max-w-5xl mx-auto">
                <motion.div
                    initial={{ opacity: 0, x: -30 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.6 }}
                >
                    <iframe
                        src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3578.498978900763!2d-48.6399038!3d-26.245464300000002!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x94d94f32b9b822f9%3A0xf9d3ce532e7d2b85!2sJL%20Despachante%20e%20Escola%20N%C3%A1utica!5e0!3m2!1spt-BR!2sbr!4v1771014809574!5m2!1spt-BR!2sbr"
                        allowFullScreen
                        loading="lazy"
                        referrerPolicy="no-referrer-when-downgrade"
                        className="w-full h-80 rounded-xl shadow-lg grayscale hover:grayscale-0 transition-all duration-500 ease-in-out border border-border"
                    />
                </motion.div>
                <motion.div
                    initial={{ opacity: 0, x: 30 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.6 }}
                    className="bg-white p-8 rounded-xl shadow-lg border border-border"
                >
                    <h3 className="font-heading text-2xl text-primary mb-6">Endereço e Contato</h3>
                    <div className="space-y-5 text-primary/90">
                        <div className="flex items-start gap-3">
                            <div className="w-10 h-10 rounded-full bg-gold/10 flex items-center justify-center flex-shrink-0">
                                <MapPin className="w-5 h-5 text-gold" />
                            </div>
                            <div>
                                <p className="font-semibold">Endereço</p>
                                <p className="text-muted-foreground">Lgo. Almirante Moraes Rego, 156 - Centro</p>
                                <p className="text-muted-foreground">São Francisco do Sul, SC</p>
                            </div>
                        </div>
                        <div className="flex items-start gap-3">
                            <div className="w-10 h-10 rounded-full bg-gold/10 flex items-center justify-center flex-shrink-0">
                                <Phone className="w-5 h-5 text-gold" />
                            </div>
                            <div>
                                <p className="font-semibold">Telefone / WhatsApp</p>
                                <p className="text-muted-foreground">(47) 99206-9885</p>
                            </div>
                        </div>
                        <div className="flex items-start gap-3">
                            <div className="w-10 h-10 rounded-full bg-gold/10 flex items-center justify-center flex-shrink-0">
                                <Mail className="w-5 h-5 text-gold" />
                            </div>
                            <div>
                                <p className="font-semibold">Email</p>
                                <p className="text-muted-foreground">jlescola.analitica@gmail.com</p>
                            </div>
                        </div>
                    </div>
                    <div className="mt-6 pt-6 border-t border-border">
                        <h4 className="font-semibold text-primary mb-2">Horário de Funcionamento</h4>
                        <div className="space-y-1 text-sm text-muted-foreground">
                            <p>Segunda a Sexta: <span className="font-medium text-primary">09h00 - 18h00</span></p>
                            <p>Sábado: <span className="font-medium text-primary">09h00 - 13h00</span></p>
                        </div>
                    </div>
                </motion.div>
            </div>
        </div>
    </section>
);


// =====================================================
// 6. CTA (Redesigned)
// =====================================================
const ContactSFSSection: React.FC = () => (
    <section id="contato-sfs" className="py-24 sm:py-32 bg-primary text-primary-foreground relative overflow-hidden">
        <div
            className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[400px] pointer-events-none animate-glow-pulse"
            style={{
                background: 'radial-gradient(ellipse, hsl(43 74% 53% / 0.1), transparent 70%)',
                filter: 'blur(40px)',
            }}
        />

        <div className="container mx-auto px-6 text-center relative z-10">
            <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6 }}
            >
                <Sparkles className="w-10 h-10 text-gold mx-auto mb-6" />
                <h2 className="font-heading text-3xl sm:text-4xl md:text-5xl mb-6">
                    Pronto para Navegar na <span className="text-gold">Baía da Babitonga</span>?
                </h2>
                <p className="max-w-2xl mx-auto text-primary-foreground/80 text-lg leading-relaxed mb-10">
                    A maior baía navegável de Santa Catarina está esperando por você. Do pescador ao turista, a JL é sua porta de entrada para o mar de São Chico.
                </p>
                <a href="https://wa.me/5547992069885?text=Olá!%20Tenho%20interesse%20nos%20serviços%20da%20JL%20Escola%20Náutica%20em%20São%20Francisco%20do%20Sul." target="_blank" rel="noopener noreferrer">
                    <Button size="lg" className="btn-gold text-lg px-10 py-6">
                        Falar com a Unidade SFS via WhatsApp
                    </Button>
                </a>
                <p className="text-sm text-primary-foreground/50 mt-6">
                    Atendimento rápido e personalizado. Resposta em até 2 horas.
                </p>
            </motion.div>
        </div>
    </section>
);


// =====================================================
// MAIN PAGE
// =====================================================
const SaoFranciscoDoSulPage: React.FC = () => {
    return (
        <>
            <Header />
            <main>
                <HeroSFS />
                <WelcomeSFSSection />
                <EcossistemaSFSSection />
                <ServicesSFSSection />
                <DestinosSFSSection />
                <WhyJLSFSSection />
                <NumerosSFSSection />
                <LocationSFSSection />
                <ContactSFSSection />
            </main>
            <Footer />
        </>
    );
};

export default SaoFranciscoDoSulPage;
